import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { selectDetailStrategies, selectDetailLoading } from "../../../../Redux/Reducers/strategyStoreSlice";
import useStrategyComparison from "./hooks/useStrategyComparison";
import StrategyCard from "./components/StrategyCard";
import ComparisonSummary from "./components/ComparisonSummary";
import UnifiedPerformanceTable from "./components/UnifiedPerformanceTable";
import ComparisonChart from "./components/ComparisonChart";
import ComparisonSkeleton from "./components/ComparisonSkeleton";

const StrategyComparison = () => {
  const navigate = useNavigate();

  const {
    ownerSlug,
    strategySlug,
    allStrategiesOptions,
    isOptionsLoading,
    handlePeriodChange,
    handleSwapCompareStrategy,
  } = useStrategyComparison();

  const strategies = useSelector(selectDetailStrategies);
  const isLoading = useSelector(selectDetailLoading);

  const hasStrategies = strategies.length >= 2;

  const location = useLocation();

  const strategyPath = location.pathname.split("/compare")[0];

  const handleBackToStrategy = () => {
    navigate(strategyPath);
  };

  return (
    <Box maxWidth='xl' sx={{mx: 'auto'}}>
      <Helmet>
        <title>Strategy Comparison | Vantage Alpha</title>
      </Helmet>

      {/* Page header */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={handleBackToStrategy}
          sx={{ ":hover": { transform: "none", boxShadow: "none", backgroundColor: "none" } }}
        >
          Back to Strategy
        </Button>
      </Box>

      {isLoading && !hasStrategies ? (
        <ComparisonSkeleton />
      ) : (
        <Box sx={{ px: 3, pb: 4 }}>
          {/* Strategy cards — one per strategy, flex row supports N cards */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            {strategies.map((strategy, index) => (
              <StrategyCard
                key={strategy.idStrategy}
                strategy={strategy}
                index={index}
                allOptions={allStrategiesOptions}
                isOptionsLoading={isOptionsLoading}
                activeStrategies={strategies}
                onSwap={(newSlug) => handleSwapCompareStrategy(index - 1, newSlug)}
              />
            ))}
          </Box>

          {hasStrategies && (
            <>
              {/* Comparison summary */}
              <Box mb={3}>
                <ComparisonSummary strategies={strategies} />
              </Box>

              {/* Performance metrics table */}
              <Card sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <UnifiedPerformanceTable strategies={strategies} />
              </Card>

              {/* Performance chart */}
              <Card sx={{ p: 3, borderRadius: 2 }}>
                <ComparisonChart onPeriodChange={handlePeriodChange} />
              </Card>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StrategyComparison;
